import {
  query,
  where,
  getDocs,
  getDoc,
  getCountFromServer,
  collectionGroup,
  orderBy,
  limit,
  updateDoc,
  serverTimestamp,
  Timestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';
import { getEffectiveTenantId, tenantPatientCollection, tenantPatientDoc } from './tenantContext';
import { getCareAppUserForPatientQueries } from './patientService';
import { createPatientEncounter } from './patientEncounterService';
import { resolveSystemEncounterSessionMinutes } from './sessionMinutesDefaultsService';
import { recordDailyWin } from '../utils/dailyWins';

function sortAlertsByLevelAndDate(arr) {
  arr.sort((a, b) => {
    const lv = (b.level || 0) - (a.level || 0);
    if (lv !== 0) return lv;
    const ta = a.createdAt?.getTime?.() ?? 0;
    const tb = b.createdAt?.getTime?.() ?? 0;
    return tb - ta;
  });
  return arr;
}

/**
 * Pending care alerts for a set of patients via per-patient subcollection queries.
 * Sorted by level (desc) then createdAt (desc).
 */
export async function getPendingCareAlertsForPatientIds(patientIds, maxTotal = 40) {
  if (!patientIds?.length) return [];
  const tenantId = await getEffectiveTenantId();
  const merged = [];
  const promises = patientIds.map(async (pid) => {
    const q = query(
      tenantPatientCollection(tenantId, pid, 'careAlerts'),
      where('status', '==', 'pending_review'),
      orderBy('createdAt', 'desc'),
      limit(80)
    );
    const snap = await getDocs(q);
    snap.docs.forEach((d) => {
      merged.push({
        id: d.id,
        patientId: pid,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate?.() ?? d.data().createdAt,
      });
    });
  });
  await Promise.all(promises);
  return sortAlertsByLevelAndDate(merged).slice(0, maxTotal);
}

/**
 * Max active alerts held by the single collection-group listener. Active alerts
 * are worked down continuously, so even a heavy overnight buildup on a
 * hundreds-of-patients roster stays well under this cap; newest-first ordering
 * keeps the most recent alerts visible if it is ever exceeded.
 */
const ACTIVE_ALERTS_LISTENER_LIMIT = 500;

function mapActiveAlertDoc(d, patientId) {
  const data = d.data();
  return {
    id: d.id,
    patientId,
    ...data,
    createdAt: data.createdAt?.toDate?.() ?? data.createdAt,
    acknowledgedAt: data.acknowledgedAt?.toDate?.() ?? data.acknowledgedAt,
    resolvedAt: data.resolvedAt?.toDate?.() ?? data.resolvedAt,
  };
}

/**
 * Real-time subscription for active (pending_review + acknowledged) care alerts.
 * Calls callback(mergedAlerts) on each snapshot update.
 * Returns an unsubscribe function.
 *
 * Roles with tenant-wide patient access use ONE collection-group listener
 * (rules already allow collection-group careAlerts reads for them) instead of
 * one listener per patient. This collapses hundreds of concurrent Listen
 * targets into a single one, which also makes transport reconnects (frequent
 * on QUIC-hostile corporate networks) dramatically cheaper. Scoped roles
 * (care manager / supervisor) keep per-patient listeners over their much
 * smaller assigned rosters, since rules can only prove their access one
 * patient document at a time.
 */
export function subscribeToActiveCareAlerts(tenantId, patientIds, callback, role) {
  if (!tenantId || !patientIds?.length) {
    callback([]);
    return () => {};
  }

  if (COLLECTION_GROUP_COUNT_ROLES.has(role)) {
    const allowed = new Set(patientIds);
    const q = query(
      collectionGroup(db, 'careAlerts'),
      where('tenantId', '==', tenantId),
      where('status', 'in', ['pending_review', 'acknowledged']),
      orderBy('createdAt', 'desc'),
      limit(ACTIVE_ALERTS_LISTENER_LIMIT),
    );
    return onSnapshot(
      q,
      (snap) => {
        const rows = [];
        snap.docs.forEach((d) => {
          const pid = d.data().patientId;
          if (!pid || !allowed.has(pid)) return;
          rows.push(mapActiveAlertDoc(d, pid));
        });
        callback(sortAlertsByLevelAndDate(rows));
      },
      (err) => {
        console.warn('Care alerts collection-group subscription error', err);
        callback([]);
      },
    );
  }

  const byPatient = new Map();
  const unsubs = [];
  const flush = () => {
    const merged = [];
    byPatient.forEach((alerts) => merged.push(...alerts));
    callback(sortAlertsByLevelAndDate(merged));
  };
  patientIds.forEach((pid) => {
    const q = query(
      tenantPatientCollection(tenantId, pid, 'careAlerts'),
      where('status', 'in', ['pending_review', 'acknowledged']),
      orderBy('createdAt', 'desc'),
      limit(80)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        byPatient.set(pid, snap.docs.map((d) => mapActiveAlertDoc(d, pid)));
        flush();
      },
      (err) => {
        console.warn('Care alerts subscription error for patient', pid, err);
        byPatient.set(pid, []);
        flush();
      },
    );
    unsubs.push(unsub);
  });
  return () => unsubs.forEach((fn) => fn());
}

export async function getPendingCareAlertsForPatientId(patientId, lim = 50) {
  if (!patientId) return [];
  const tenantId = await getEffectiveTenantId();
  const q = query(
    tenantPatientCollection(tenantId, patientId, 'careAlerts'),
    where('status', '==', 'pending_review'),
    orderBy('createdAt', 'desc'),
    limit(lim)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    id: d.id,
    patientId,
    ...d.data(),
    createdAt: d.data().createdAt?.toDate?.() ?? d.data().createdAt,
  }));
}

export async function acknowledgeCareAlert(patientId, alertId, userId, userName, profile) {
  if (!patientId || !alertId || !userId) throw new Error('Missing patient, alert, or user.');
  const tenantId = await getEffectiveTenantId();
  const alertRef = tenantPatientDoc(tenantId, patientId, 'careAlerts', alertId);
  const alertSnap = await getDoc(alertRef);
  const alertData = alertSnap.exists() ? alertSnap.data() : {};
  await updateDoc(alertRef, {
    status: 'acknowledged',
    acknowledgedAt: serverTimestamp(),
    acknowledgedBy: userId,
    acknowledgedByName: userName || '',
  });
  const alertTitle = alertData.title || 'Care Alert';
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
  const sessionMinutes = await resolveSystemEncounterSessionMinutes('care_alert_acknowledged');
  createPatientEncounter({
    patientId,
    bodyHtml: `<p><strong>Care alert acknowledged:</strong> ${alertTitle} (Level ${alertData.level || '?'})</p>`,
    effectiveDate: today,
    encounterType: 'care_alert_acknowledged',
    profile,
    isSystemGenerated: true,
    systemReason: 'care_alert_acknowledged',
    sessionMinutes,
  }).catch((e) => console.warn('Auto-encounter for care alert ack failed', e));
}

export async function resolveCareAlert(patientId, alertId, userId, userName, resolutionNote, profile) {
  if (!patientId || !alertId || !userId) throw new Error('Missing patient, alert, or user.');
  const tenantId = await getEffectiveTenantId();
  const alertRef = tenantPatientDoc(tenantId, patientId, 'careAlerts', alertId);
  const alertSnap = await getDoc(alertRef);
  const alertData = alertSnap.exists() ? alertSnap.data() : {};
  await updateDoc(alertRef, {
    status: 'resolved',
    resolvedAt: serverTimestamp(),
    resolvedBy: userId,
    resolvedByName: userName || '',
    resolutionNote: resolutionNote || '',
  });
  recordDailyWin({
    uid: userId,
    type: 'alertResolved',
    patientId,
    eventId: `alert:${patientId}:${alertId}`,
  });
  const alertTitle = alertData.title || 'Care Alert';
  const note = resolutionNote ? `<p>${resolutionNote}</p>` : '';
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
  const sessionMinutes = await resolveSystemEncounterSessionMinutes('care_alert_resolved');
  createPatientEncounter({
    patientId,
    bodyHtml: `<p><strong>Care alert resolved:</strong> ${alertTitle} (Level ${alertData.level || '?'})</p>${note}`,
    effectiveDate: today,
    encounterType: 'care_alert_resolved',
    profile,
    isSystemGenerated: true,
    systemReason: 'care_alert_resolved',
    sessionMinutes,
  }).catch((e) => console.warn('Auto-encounter for care alert resolve failed', e));
}

/**
 * One-click acknowledge + resolve for an alert (used by the "Ack & Resolve" button and the
 * "Resolve all historical" bulk action). Runs the normal two-step flow back-to-back so it stays
 * within the Firestore security rules (pending_review -> acknowledged -> resolved) and logs both
 * encounters. Safe to call on a pending_review or already-acknowledged alert; a no-op if resolved.
 */
export async function acknowledgeAndResolveCareAlert(patientId, alertId, userId, userName, resolutionNote, profile) {
  if (!patientId || !alertId || !userId) throw new Error('Missing patient, alert, or user.');
  const tenantId = await getEffectiveTenantId();
  const alertRef = tenantPatientDoc(tenantId, patientId, 'careAlerts', alertId);
  const alertSnap = await getDoc(alertRef);
  const status = alertSnap.exists() ? alertSnap.data().status : null;
  if (status === 'resolved') return;
  if (status !== 'acknowledged') {
    await acknowledgeCareAlert(patientId, alertId, userId, userName, profile);
  }
  await resolveCareAlert(patientId, alertId, userId, userName, resolutionNote || 'Acknowledged & resolved', profile);
}

function mapCareAlertDoc(d) {
  const data = d.data();
  return {
    id: d.id,
    ...data,
    createdAt: data.createdAt?.toDate?.() ?? data.createdAt,
    acknowledgedAt: data.acknowledgedAt?.toDate?.() ?? data.acknowledgedAt,
    resolvedAt: data.resolvedAt?.toDate?.() ?? data.resolvedAt,
  };
}

/**
 * Full alert history for a patient (pending and acknowledged), newest first.
 */
export async function getCareAlertsHistoryForPatient(patientId, maxRows = 300) {
  if (!patientId) return [];
  const tenantId = await getEffectiveTenantId();
  const lim = Math.min(Math.max(1, maxRows), 500);
  const q = query(
    tenantPatientCollection(tenantId, patientId, 'careAlerts'),
    orderBy('createdAt', 'desc'),
    limit(lim)
  );
  const snap = await getDocs(q);
  return snap.docs.map(mapCareAlertDoc);
}

/**
 * Acknowledged alerts in a tenant where acknowledgement occurred in [start, end].
 * Queries each patient's careAlerts subcollection individually.
 */
export async function getAcknowledgedCareAlertsInAcknowledgementWindow(
  tenantId,
  patientIds,
  start,
  end,
  maxRows = 500
) {
  if (!tenantId || !start || !end || !patientIds?.length) return [];
  const lim = Math.min(Math.max(1, maxRows), 500);
  const startTs = Timestamp.fromDate(start);
  const endTs = Timestamp.fromDate(end);
  const merged = [];
  const promises = patientIds.map(async (pid) => {
    const q = query(
      tenantPatientCollection(tenantId, pid, 'careAlerts'),
      where('status', 'in', ['acknowledged', 'resolved']),
      where('acknowledgedAt', '>=', startTs),
      where('acknowledgedAt', '<=', endTs),
      orderBy('acknowledgedAt', 'desc'),
      limit(lim)
    );
    const snap = await getDocs(q);
    snap.docs.forEach((d) => merged.push({ patientId: pid, ...mapCareAlertDoc(d) }));
  });
  await Promise.all(promises);
  merged.sort((a, b) => {
    const ta = a.acknowledgedAt?.getTime?.() ?? 0;
    const tb = b.acknowledgedAt?.getTime?.() ?? 0;
    return tb - ta;
  });
  // `maxRows` caps each patient's query, not the tenant-wide total.
  return merged;
}

/**
 * Max in-flight count aggregations. Unbounded fan-out (2 requests per patient,
 * concurrent with the dashboard's other per-patient queries) saturated the
 * Firestore WebChannel transport ("Connection failed.") on large rosters.
 */
const ALERT_COUNT_CONCURRENCY = 50;

/** Firestore `in` filter limit. */
const PATIENT_ID_IN_CHUNK = 30;

/**
 * Roles allowed by rules to run collection-group careAlerts queries. These roles
 * already have unrestricted per-patient access to every patient in their tenant,
 * so the collection-group path grants no new data — it only batches the counts
 * (2 requests per 30 patients instead of 2 per patient).
 */
const COLLECTION_GROUP_COUNT_ROLES = new Set(['admin', 'clinical admin', 'superuser']);

/** Sum {resolved, total} counts via collection-group aggregations chunked by patientId. */
async function countAlertsViaCollectionGroup(tenantId, ids, startTs, endTs) {
  const chunks = [];
  for (let i = 0; i < ids.length; i += PATIENT_ID_IN_CHUNK) {
    chunks.push(ids.slice(i, i + PATIENT_ID_IN_CHUNK));
  }
  const counts = await Promise.all(chunks.map(async (chunk) => {
    const base = [
      where('tenantId', '==', tenantId),
      where('patientId', 'in', chunk),
      where('createdAt', '>=', startTs),
      where('createdAt', '<=', endTs),
      orderBy('createdAt', 'desc'),
    ];
    const [totalAgg, resolvedAgg] = await Promise.all([
      getCountFromServer(query(collectionGroup(db, 'careAlerts'), ...base)),
      getCountFromServer(query(collectionGroup(db, 'careAlerts'), where('status', '==', 'resolved'), ...base)),
    ]);
    return { total: totalAgg.data().count, resolved: resolvedAgg.data().count };
  }));
  let resolved = 0;
  let total = 0;
  for (const c of counts) {
    resolved += c.resolved;
    total += c.total;
  }
  return { resolved, total };
}

/** Sum {resolved, total} counts via per-patient aggregations with capped concurrency. */
async function countAlertsPerPatient(tenantId, ids, startTs, endTs) {
  const countForPatient = async (pid) => {
    const col = tenantPatientCollection(tenantId, pid, 'careAlerts');
    const totalQ = query(
      col,
      where('createdAt', '>=', startTs),
      where('createdAt', '<=', endTs),
    );
    // orderBy is required so this matches the deployed (status ASC, createdAt DESC)
    // composite index; without it Firestore implies createdAt ASC and the query fails.
    const resolvedQ = query(
      col,
      where('status', '==', 'resolved'),
      where('createdAt', '>=', startTs),
      where('createdAt', '<=', endTs),
      orderBy('createdAt', 'desc'),
    );
    const [totalAgg, resolvedAgg] = await Promise.all([
      getCountFromServer(totalQ),
      getCountFromServer(resolvedQ),
    ]);
    return {
      total: totalAgg.data().count,
      resolved: resolvedAgg.data().count,
    };
  };

  let resolved = 0;
  let total = 0;
  for (let i = 0; i < ids.length; i += ALERT_COUNT_CONCURRENCY) {
    const slice = ids.slice(i, i + ALERT_COUNT_CONCURRENCY);
    const counts = await Promise.all(slice.map(countForPatient));
    for (const c of counts) {
      resolved += c.resolved;
      total += c.total;
    }
  }
  return { resolved, total };
}

/**
 * All care alerts created within [start, end] for the given patient IDs (any status).
 * Uses server-side count aggregations (no document downloads).
 * Returns { resolved, total } counts.
 */
export async function getCareAlertResolutionCountsInRange(patientIds, start, end) {
  const ids = [...new Set((patientIds || []).filter(Boolean))];
  if (!ids.length || !start || !end) return { resolved: 0, total: 0 };
  const [user, tenantId] = await Promise.all([
    getCareAppUserForPatientQueries(),
    getEffectiveTenantId(),
  ]);
  const startTs = Timestamp.fromDate(start);
  const endTs = Timestamp.fromDate(end);
  if (user && COLLECTION_GROUP_COUNT_ROLES.has(user.role)) {
    return countAlertsViaCollectionGroup(tenantId, ids, startTs, endTs);
  }
  return countAlertsPerPatient(tenantId, ids, startTs, endTs);
}

/**
 * Alerts with createdAt in [start,end] per patient—full rows for analytics (resolution times, cohort volume).
 */
export async function getCareAlertsCreatedInRange(tenantId, patientIds, start, end, maxRowsPerPatient = 500) {
  if (!tenantId || !patientIds?.length || !start || !end) return [];
  const startTs = Timestamp.fromDate(start);
  const endTs = Timestamp.fromDate(end);
  const lim = Math.min(Math.max(1, maxRowsPerPatient), 500);
  const merged = [];
  const promises = patientIds.map(async (pid) => {
    const q = query(
      tenantPatientCollection(tenantId, pid, 'careAlerts'),
      where('createdAt', '>=', startTs),
      where('createdAt', '<=', endTs),
      orderBy('createdAt', 'desc'),
      limit(lim)
    );
    const snap = await getDocs(q);
    snap.docs.forEach((d) => merged.push({ patientId: pid, ...mapCareAlertDoc(d) }));
  });
  await Promise.all(promises);
  merged.sort((a, b) => {
    const tb = b.createdAt?.getTime?.() ?? 0;
    const ta = a.createdAt?.getTime?.() ?? 0;
    return tb - ta;
  });
  return merged;
}
